import { InjectionToken, makeStateKey, StateKey, TransferState } from '@angular/core';
import { type JsonType } from 'posthog-js';

/**
 * What the SSR render evaluated, handed to the browser so the first paint and the first client-side
 * read agree. `personProperties` travels with the values because a property-targeted flag re-read
 * against different inputs flips arm silently, which corrupts an experiment rather than breaking it.
 */
export interface PostHogBootstrapState {
  distinctId: string;
  featureFlags: Record<string, boolean | string>;
  featureFlagPayloads: Record<string, JsonType>;
  personProperties: Record<string, string>;
}

/** Mirrors `ld_flags`, which the LaunchDarkly SSR service writes for the same handover. */
export const POSTHOG_FLAG_STATE_KEY: StateKey<PostHogBootstrapState | undefined> = makeStateKey<PostHogBootstrapState | undefined>(
  'posthog_flags',
);

/** Supplied per render by the Express layer, which is where `posthog-node` lives. */
export const POSTHOG_SSR_FLAG_STATE = new InjectionToken<PostHogBootstrapState | null>('POSTHOG_SSR_FLAG_STATE');

/**
 * Removes the key as it reads. Left in place it would be serialised into the DOM a second time by a
 * later render and would keep bootstrapping stale values into any subsequent hydration.
 */
export function takePostHogBootstrapState(state: TransferState): PostHogBootstrapState | undefined {
  if (!state.hasKey(POSTHOG_FLAG_STATE_KEY)) {
    return undefined;
  }

  const value = state.get(POSTHOG_FLAG_STATE_KEY, undefined);
  state.remove(POSTHOG_FLAG_STATE_KEY);

  return value;
}
